import { ScoreValue, ValueScale, DEFAULT_VALUE_SCALE } from '@/types/evaluation.types';

/**
 * Interface for value scale implementations.
 * Allows adding new scales in the future without changing core logic.
 */
export interface IValueScale {
  mapRawToValue(raw: ScoreValue): number | null;
  getScaleName(): string;
}

/**
 * Linear 1-to-5 scale implementation.
 * Maps raw scores (1-5) to normalized values (0-1).
 *
 * Mapping:
 * - 1 → 0.00 (Unacceptable)
 * - 2 → 0.25 (Does not meet expectations)
 * - 3 → 0.50 (Partially meets expectations)
 * - 4 → 0.75 (Meets Expectations)
 * - 5 → 1.00 (Exceeds Expectations)
 */
export class Linear1To5Scale implements IValueScale {
  mapRawToValue(raw: ScoreValue): number | null {
    // Exclude null (not scored) and 0 (skip)
    if (raw === null || raw === 0) {
      return null;
    }

    // Map 1-5 to 0-1 using formula: (raw - 1) / 4
    return (raw - 1) / 4;
  }

  getScaleName(): string {
    return DEFAULT_VALUE_SCALE;
  }
}

/**
 * Factory to get scale implementation by name.
 *
 * @param scaleName Name of the value scale
 * @returns Value scale implementation
 * @throws Error if scale name is unknown
 */
export function getValueScale(scaleName: ValueScale): IValueScale {
  switch (scaleName) {
    case DEFAULT_VALUE_SCALE:
      return new Linear1To5Scale();
    default:
      throw new Error(`Unknown value scale: ${scaleName}`);
  }
}

/**
 * Helper function to map raw score to normalized value.
 *
 * @param raw Raw score value (0-5 or null)
 * @param scale Value scale to use (defaults to DEFAULT_VALUE_SCALE)
 * @returns Normalized value (0-1) or null if raw is null/0
 */
export function mapRawToValue(
  raw: ScoreValue,
  scale: ValueScale = DEFAULT_VALUE_SCALE
): number | null {
  const scaleImpl = getValueScale(scale);
  return scaleImpl.mapRawToValue(raw);
}
